import { useEffect, useRef } from "react";

import type {
  CommandDisbandCardView,
  CommandViewModel,
  LaneViewId,
} from "../presentation/game-view-model";

interface CommandDisbandPanelProps {
  command: CommandViewModel;
  eventLimit: number;
  selectedLaneId: LaneViewId;
  selectedCardInstanceId: string | undefined;
  validationMessage: string | undefined;
  onSelectCard: (cardInstanceId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function choiceLabel(choice: CommandDisbandCardView): string {
  return `${choice.name}, ${choice.type === "army" ? "Army" : "Commander"}. Select to disband voluntarily.`;
}

function confirmationCopy(choice: CommandDisbandCardView): string {
  if (choice.type === "commander") {
    return "This Commander will enter your discard pile. Armies remain as a garrison. This lane will no longer attack or authorize an Event.";
  }
  return "This Army will enter your discard pile. Previously paid Upkeep is not refunded.";
}

export function CommandDisbandPanel({
  command,
  eventLimit,
  selectedLaneId,
  selectedCardInstanceId,
  validationMessage,
  onSelectCard,
  onConfirm,
  onCancel,
}: CommandDisbandPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const previewRef = useRef<HTMLParagraphElement>(null);
  const lane = command.lanes.find((candidate) => candidate.laneId === selectedLaneId);
  const selectedCard = lane?.legalChoices.find(
    (choice) => choice.instanceId === selectedCardInstanceId,
  );
  const visibleChoices = selectedCard === undefined ? (lane?.legalChoices ?? []) : [selectedCard];

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (panelRef.current !== null) panelRef.current.scrollTop = 0;
      titleRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [command.legalDisbandActionCount, selectedLaneId]);

  useEffect(() => {
    if (selectedCard !== undefined) {
      previewRef.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [selectedCard]);

  return (
    <section
      ref={panelRef}
      className="command-disband"
      aria-labelledby="command-disband-title"
      data-testid="command-disband-panel"
    >
      <header className="command-disband__header">
        <div>
          <span className="eyebrow">Voluntary Command choice</span>
          <h2 id="command-disband-title" ref={titleRef} tabIndex={-1}>
            {lane?.label ?? "Selected"} lane - Manage forces
          </h2>
        </div>
        <div className="command-disband__badges" aria-label="Disband consequences">
          <span>No action cost</span>
          <span>No refund</span>
        </div>
      </header>

      {lane === undefined ? null : (
        <dl className="command-lane-summary" aria-label={`${lane.label} lane current totals`}>
          <div>
            <dt>Leader</dt>
            <dd>{lane.commanderPresent ? "Commander" : "Garrison"}</dd>
          </div>
          <div>
            <dt>Attack / Guard</dt>
            <dd>
              {lane.totalAttack} / {lane.totalGuard}
            </dd>
          </div>
          <div>
            <dt>Event Limit</dt>
            <dd>{eventLimit}</dd>
          </div>
        </dl>
      )}

      <div className="command-disband__body">
        {lane === undefined ? (
          <p className="command-disband__empty">This lane is unavailable.</p>
        ) : (
          <>
            {lane.legalChoices.length === 0 ? (
              <p className="command-disband__empty">
                No active Commander or Army in this lane can be voluntarily disbanded.
              </p>
            ) : (
              <div className="command-disband__choices" aria-label="Cards available to disband">
                {visibleChoices.map((choice) => (
                  <button
                    key={choice.instanceId}
                    className="command-disband-card"
                    type="button"
                    aria-label={choiceLabel(choice)}
                    aria-pressed={choice.instanceId === selectedCardInstanceId}
                    data-card-kind={choice.type}
                    data-testid={`command-${choice.type}-choice`}
                    onClick={() => onSelectCard(choice.instanceId)}
                  >
                    <span>{choice.type === "army" ? "Army" : "Commander"}</span>
                    <strong>{choice.name}</strong>
                    {choice.armySize === undefined ? null : <span>Size {choice.armySize}</span>}
                  </button>
                ))}
              </div>
            )}

            <p
              className="command-disband__preview"
              ref={previewRef}
              aria-live="polite"
              data-testid="command-disband-consequence"
            >
              {selectedCard === undefined ? (
                "Choose a Commander or Army. Disbanding is voluntary and uses no action."
              ) : (
                <>
                  <strong>Disband {selectedCard.name}?</strong> {confirmationCopy(selectedCard)}
                </>
              )}
            </p>
          </>
        )}
      </div>

      {validationMessage ? (
        <p className="validation-message" role="status">
          {validationMessage}
        </p>
      ) : null}

      <div className="command-disband__actions">
        <button className="action-button" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="action-button action-button--primary"
          type="button"
          data-testid="confirm-command-disband"
          disabled={selectedCard === undefined}
          onClick={onConfirm}
        >
          Confirm disband
        </button>
      </div>
    </section>
  );
}
